/**
 * @file Modal.js
 * @description Componente visual para alertas e confirmações.
 */
import { useEffect } from "react";
import { X, AlertTriangle, CheckCircle, Info, HelpCircle } from "lucide-react";

const typeConfig = {
  success: {
    icon: <CheckCircle className="h-5 w-5 text-green-400" />,
    headerCls: "bg-green-500/10 border-green-500/20",
  },
  error: {
    icon: <AlertTriangle className="h-5 w-5 text-red-400" />,
    headerCls: "bg-red-500/10 border-red-500/20",
  },
  info: {
    icon: <Info className="h-5 w-5 text-blue-400" />,
    headerCls: "bg-blue-500/10 border-blue-500/20",
  },
  confirm: {
    icon: <HelpCircle className="h-5 w-5 text-yellow-400" />,
    headerCls: "bg-yellow-500/10 border-yellow-500/20",
  },
};

const Modal = ({ isOpen, onClose, title, message, type = "info", onConfirm }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isConfirm = type === "confirm";
  const cfg = typeConfig[type] || typeConfig.info;

  return (
    <div
      className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex justify-center items-end sm:items-center p-0 sm:p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-campo-900 border border-campo-700 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md">
        {/* Header colorido por tipo */}
        <div className={`p-5 rounded-t-2xl border-b ${cfg.headerCls}`}>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">{cfg.icon}</div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-white leading-tight">{title}</h3>
              <p className="mt-1 text-sm text-gray-300 leading-relaxed">{message}</p>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:bg-campo-700 hover:text-white transition-colors"
              aria-label="Fechar"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Ações */}
        <div className="px-5 py-4 flex flex-row-reverse gap-2">
          {isConfirm ? (
            <>
              <button
                type="button"
                onClick={onConfirm}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                Confirmar
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 bg-campo-700 hover:bg-campo-600 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                Cancelar
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 bg-ouro hover:bg-ouro-light text-campo-950 text-sm font-bold rounded-lg transition-colors"
            >
              OK
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;
